import React from 'react';
import { useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import { PageHeader, SectionContent, StyledList } from '../../components';
import { MODERN_SLAVERY } from '../../data';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const ModernSlaveryPage = ({ setRefs }) => {
  const modernSlaveryRef = useRef(null);
  const commitmentRef = useRef(null);
  const businessRef = useRef(null);
  const riskRef = useRef(null);
  const stepsRef = useRef(null);
  const policiesRef = useRef(null);
  const conclusionRef = useRef(null);

  const refs = useMemo(
    () => ({
      modernSlavery: modernSlaveryRef,
      commitment: commitmentRef,
      business: businessRef,
      risk: riskRef,
      steps: stepsRef,
      policies: policiesRef,
      conclusion: conclusionRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  return (
    <>
      <SEO
        title={seoData.modernSlavery.title}
        content={seoData.modernSlavery.content}
        keywords={seoData.modernSlavery.keywords}
        canonical={seoData.modernSlavery.canonical}
        ogImage={seoData.modernSlavery.ogImage}
      />
      {/* Page Header */}
      <PageHeader
        id='hero'
        title={MODERN_SLAVERY.TITLE}
        align='left'
        sx={{ mb: SECTION_SPACING }}
      />

      {/* Introduction */}
      <SectionContent
        id='modernSlavery'
        ref={refs.modernSlavery}
        initialSpacing={false}
        text={[
          {
            text: MODERN_SLAVERY.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic',
            fontSize: '1.1rem',
            color: THEME.COLORS.NEUTRAL_GRAY_DARK
          },
          {
            text: MODERN_SLAVERY.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      {/* Our Commitment section */}
      <SectionContent
        id='commitment'
        ref={refs.commitment}
        title={MODERN_SLAVERY.SECTIONS.COMMITMENT.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={MODERN_SLAVERY.SECTIONS.COMMITMENT.PARAGRAPHS.map(paragraph => ({
          text: paragraph,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />

      {/* Our Business section */}
      <SectionContent
        id='business'
        ref={refs.business}
        title={MODERN_SLAVERY.SECTIONS.BUSINESS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={MODERN_SLAVERY.SECTIONS.BUSINESS.PARAGRAPHS.map(paragraph => ({
          text: paragraph,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />

      {/* Risk of Modern Slavery and Human Trafficking section */}
      <SectionContent
        id='risk'
        ref={refs.risk}
        title={MODERN_SLAVERY.SECTIONS.RISK.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={MODERN_SLAVERY.SECTIONS.RISK.PARAGRAPHS.map(paragraph => ({
          text: paragraph,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />

      {/* Steps Taken to Combat Modern Slavery section */}
      <SectionContent
        id='steps'
        ref={refs.steps}
        title={MODERN_SLAVERY.SECTIONS.STEPS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: MODERN_SLAVERY.SECTIONS.STEPS.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={MODERN_SLAVERY.SECTIONS.STEPS.LIST_ITEMS} />
      </Box>

      {/* Policies and Procedures section */}
      <SectionContent
        id='policies'
        ref={refs.policies}
        title={MODERN_SLAVERY.SECTIONS.POLICIES.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[]}
      />

      <Box sx={{ mb: ELEMENT_SPACING }}>
        <StyledList items={MODERN_SLAVERY.SECTIONS.POLICIES.LIST_ITEMS} />
      </Box>

      {/* Conclusion section */}
      <SectionContent
        id='conclusion'
        ref={refs.conclusion}
        title={MODERN_SLAVERY.SECTIONS.CONCLUSION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={MODERN_SLAVERY.SECTIONS.CONCLUSION.PARAGRAPHS.map(paragraph => ({
          text: paragraph,
          marginBottom: ELEMENT_SPACING,
          fontSize: '1.3rem'
        }))}
      />

      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {MODERN_SLAVERY.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default ModernSlaveryPage;
